import { Box, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { Table } from 'Common/MUITableV2'
import { GridSkeleton } from './GridSkeleton.view'
import { autoHeight } from 'Common/utils/utils'
import {
  DAY_LABELS,
  formatNumber,
  formatPct,
  useUtilisationData,
} from './Utilisation.data'
import type { ConstraintUtilisationRow } from './Utilisation.types'

type Props = {
  schedulerId: string
  iteration: string
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

type Measure = 'avg' | 'max' | 'max_pct'

type PivotedRow = {
  id: string
  resource_name: string
  capacity: number
  measure: string
  measureKind: Measure
  mon: number
  tue: number
  wed: number
  thu: number
  fri: number
  sat: number
  sun: number
}

const MEASURE_LABEL: Record<Measure, string> = {
  avg: 'Average Number per Hour',
  max: 'Max Number per Hour',
  max_pct: 'Max % of Constraint',
}

const blankPivot = (
  resource_name: string,
  capacity: number,
  measureKind: Measure
): PivotedRow => ({
  id: `${resource_name}|${measureKind}`,
  resource_name,
  capacity,
  measure: MEASURE_LABEL[measureKind],
  measureKind,
  mon: 0,
  tue: 0,
  wed: 0,
  thu: 0,
  fri: 0,
  sat: 0,
  sun: 0,
})

const pivot = (rows: ConstraintUtilisationRow[]): PivotedRow[] => {
  const byResource = new Map<string, Record<Measure, PivotedRow>>()
  for (const r of rows) {
    const dayKey = DAY_KEYS[r.day_of_week]
    if (!dayKey) continue
    let bucket = byResource.get(r.resource_name)
    if (!bucket) {
      bucket = {
        avg: blankPivot(r.resource_name, r.capacity, 'avg'),
        max: blankPivot(r.resource_name, r.capacity, 'max'),
        max_pct: blankPivot(r.resource_name, r.capacity, 'max_pct'),
      }
      byResource.set(r.resource_name, bucket)
    }
    bucket.avg[dayKey] = r.avg_concurrent
    bucket.max[dayKey] = r.max_concurrent
    bucket.max_pct[dayKey] = r.max_pct_of_constraint
  }
  const out: PivotedRow[] = []
  for (const name of Array.from(byResource.keys()).sort()) {
    const bucket = byResource.get(name)!
    out.push(bucket.avg, bucket.max, bucket.max_pct)
  }
  return out
}

export const ConstraintUtilisationView = ({
  schedulerId,
  iteration,
}: Props) => {
  const { data, isLoading, error } = useUtilisationData(schedulerId, iteration)

  const rows = useMemo(
    () => (data ? pivot(data.constraint_utilisation) : []),
    [data]
  )

  const cols = useMemo<GridColDef<PivotedRow>[]>(
    () => [
      {
        field: 'resource_name',
        headerName: 'Resource',
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'capacity',
        headerName: 'Constraint Value',
        type: 'number',
        flex: 1,
        minWidth: 130,
        valueFormatter: (value: number) => formatNumber(value, 0),
      },
      { field: 'measure', headerName: 'Measure', flex: 1, minWidth: 200 },
      ...DAY_KEYS.map<GridColDef<PivotedRow>>((key, idx) => ({
        field: key,
        headerName: DAY_LABELS[idx],
        type: 'number',
        flex: 1,
        minWidth: 90,
        valueFormatter: (value: number, row: PivotedRow) =>
          row.measureKind === 'max_pct'
            ? formatPct(value)
            : formatNumber(value, 2),
      })),
    ],
    []
  )

  // `cols` is memoised on [] — the day columns come from DAY_KEYS, not from
  // the response — so the skeleton can show the real column layout even
  // though the rows are a pivot of data that has not arrived.
  if (isLoading) return <GridSkeleton columns={cols as GridColDef[]} />
  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load utilisation data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className="table-container-mui">
      <Table
        ref={(el: HTMLDivElement | null) => el && autoHeight(el)}
        rows={rows}
        columns={cols as GridColDef[]}
        density="compact"
        disableRowSelectionOnClick
        getRowClassName={(p) =>
          (p.indexRelativeToCurrentPage ?? 0) % 2 === 0
            ? 'row--even'
            : 'row--odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        pageSizeOptions={[25, 50, 100]}
      />
    </Box>
  )
}
